import { HttpClient, HttpHeaders } from '@angular/common/http';
import { InjectionToken } from '@angular/core';
import { FirebaseOptions } from '@firebase/app';
import MtbPlugin from '@ridango/core/lib/services/mtb.plugin';
import Env from '@ridango/core/env';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export const FIREBASE_CONFIG = new InjectionToken<FirebaseOptions>('firebase-config');
export const FIREBASE_CONFIG_PROVIDER = {
    provide: FIREBASE_CONFIG,
    deps: ['ENV'],
    useFactory: (env) => {
        const defaultFirebaseConfig = {
            projectId: 'DEFAULT',
            apiKey: 'DEFAULT',
            appId: 'DEFAULT',
            messagingSenderId: 'DEFAULT'
        };
        return { ...defaultFirebaseConfig, ...env.FIREBASE };
    }
};

export abstract class FirebaseNotificationServiceFacade {
    private coreService: FirebaseNotificationService;
    private wmbService: FirebaseWMBNotificationService;
    protected constructor(
        protected env: Env,
        protected http: HttpClient
    ) {
        this.coreService = new FirebaseNotificationService(env, http);
        this.wmbService = new FirebaseWMBNotificationService(env, http);
    }

    abstract initialize(): void;

    abstract unsubscribe(): Promise<void>;

    async checkinRegistrationToken(
        regionId: string | number,
        firebaseProjectId: string,
        registrationToken: string
    ): Promise<void> {
        if (this.env.IS_NEW_AFC) {
            await this.wmbService.saveRegistrationToken(firebaseProjectId, registrationToken);
        } else {
            try {
                await this.coreService.checkinRegistrationToken(Number(regionId), firebaseProjectId, registrationToken);
                localStorage.setItem(
                    'checkinRegistrationTokenApiCall',
                    `Success at ${new Date().toLocaleTimeString()}`
                );
            } catch (error) {
                localStorage.setItem('checkinRegistrationTokenApiCall', `Failed at ${new Date().toLocaleTimeString()}`);
                console.error('Error in checkinRegistrationToken:', error);
                throw new Error(`Failed to checkin registration token: ${error.message}`);
            }
        }
    }

    // TODO: local storage item to be removed after testing
    async removeFirebaseToken(regionId: number, firebaseProjectId: string, registrationToken: string): Promise<void> {
        if (this.env.IS_NEW_AFC) {
            return;
        }

        await this.coreService
            .removeFirebaseToken(regionId, firebaseProjectId, registrationToken)
            .then(() => {
                localStorage.setItem('removeFirebaseToken', `Success at ${new Date().toLocaleTimeString()}`);
            })
            .catch(() => {
                localStorage.setItem('removeFirebaseToken', `Failed at ${new Date().toLocaleTimeString()}`);
            });
    }
}

class FirebaseNotificationService {
    constructor(
        private env: Env,
        private http: HttpClient
    ) {}

    async checkinRegistrationToken(
        regionId: number,
        firebaseProjectId: string,
        registrationToken: string
    ): Promise<void> {
        const encodedRegionId = encodeURIComponent(regionId);
        const encodedProjectId = encodeURIComponent(firebaseProjectId);
        const url = `${this.env.CORE_API_URL}/firebase/v1/region/${encodedRegionId}/project/${encodedProjectId}/registration-token/checkin`;

        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const options = {
            headers: headers
        };

        const requestBody = {
            registration_token: registrationToken
        };

        localStorage.setItem(
            'checkinRegistrationTokenRequest',
            JSON.stringify({
                url,
                headers,
                requestBody
            })
        );

        await this.http.put<void>(url, requestBody, options).toPromise();
    }

    async removeFirebaseToken(regionId: number, firebaseProjectId: string, registrationToken: string): Promise<void> {
        const encodedRegionId = encodeURIComponent(regionId);
        const encodedProjectId = encodeURIComponent(firebaseProjectId);
        const url = `${this.env.CORE_API_URL}/firebase/v1/region/${encodedRegionId}/project/${encodedProjectId}/registration-token/remove/all`;

        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const options = {
            headers: headers
        };

        const requestBody = {
            registration_tokens: [registrationToken]
        };

        await this.http.put<void>(url, requestBody, options).toPromise();
    }
}

class FirebaseWMBNotificationService {
    constructor(
        private env: Env,
        private http: HttpClient
    ) {}

    async saveRegistrationToken(firebaseProjectId: string, registrationToken: string): Promise<void> {
        const encodedProjectId = encodeURIComponent(firebaseProjectId);
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/notification-manager/firebase/project/${encodedProjectId}/registration-token`;

        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const options = {
            headers: headers
        };
        const { deviceId } = await MtbPlugin.deviceId();
        const requestBody = {
            customerDeviceId: deviceId,
            token: registrationToken
        };

        await this.http
            .post<void>(url, requestBody, options)
            .pipe(
                catchError((err) => {
                    console.error(err);
                    return of();
                })
            )
            .toPromise();
    }
}
